import { useEffect, useState } from "react";
import Modal from "../../../../../../components/base/Modal";
import type { Device, ThemeClasses } from "../types";
import {
  useInspectDeviceMeta,
  useApplyDeviceMetaRefresh,
  type DeviceMetaInspectResult,
} from "../../../../../../features/devices/mutations";
import { useAllBrands, useStatuses } from "../../../../../../features/catalogs/queries";
import { useBrandsStore } from "../../../../../../store/reseller-admin/brandsStore";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { getError } from "../../../../../../utils/getError";

interface DeviceMetaRefreshModalProps {
  device: Device;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
}

export function DeviceMetaRefreshModal({
  device,
  themeClasses,
  isDarkMode,
  onClose,
}: DeviceMetaRefreshModalProps) {
  const imei = device.identifier || "";

  const inspect = useInspectDeviceMeta();
  const apply = useApplyDeviceMetaRefresh();

  const [result, setResult] = useState<DeviceMetaInspectResult | null>(null);
  const [applied, setApplied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useAllBrands();
  useStatuses();
  const allBrands = useBrandsStore((s) => s.allBrands);
  const statuses = useStatusesStore((s) => s.statuses);
  const allCompanies = useCompaniesStore((s) => s.allCompanies);
  const fetchAllCompanies = useCompaniesStore((s) => s.fetchAllCompanies);

  const runInspect = () => {
    if (!imei) return;
    inspect.mutate(imei, { onSuccess: (data) => setResult(data) });
  };

  useEffect(() => {
    runInspect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imei]);

  useEffect(() => {
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  const brandLabel = (id?: string | null) => {
    if (!id) return "—";
    const b = allBrands.find((x) => x.id === id);
    return b ? `${b.brand} ${b.model}`.trim() : id;
  };
  const statusLabel = (id?: string | null) => {
    if (!id) return "—";
    return statuses.find((x) => x.id === id)?.name || id;
  };
  const companyLabel = (id?: string | null) => {
    if (!id) return "—";
    return allCompanies.find((x) => x.id === id)?.name || id;
  };
  const orDash = (v?: string | null) => v || "—";

  const cardBg = isDarkMode ? "bg-dark-card" : "bg-gray-50";

  const handleApply = () => {
    if (!imei) return;
    apply.mutate(imei, {
      onSuccess: () => setApplied(true),
    });
  };

  const renderBody = () => {
    if (inspect.isPending && !result) {
      return (
        <div className="flex items-center justify-center gap-3 py-10">
          <div className="animate-spin h-5 w-5 border-2 border-t-transparent border-primary-500 rounded-full" />
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            Consultando datos de la fuente...
          </span>
        </div>
      );
    }

    if (inspect.isError) {
      return (
        <div className="py-8 text-center">
          <i className="ri-error-warning-line text-3xl text-red-500" />
          <p className={`mt-2 text-sm ${themeClasses.text}`}>
            No se pudieron consultar los datos.
          </p>
          <p className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
            {getError(inspect.error)}
          </p>
          <button
            onClick={runInspect}
            className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium"
          >
            Reintentar
          </button>
        </div>
      );
    }

    if (!result) return null;

    if (result.hasDuplicates) {
      return (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
            <i className="ri-error-warning-line text-amber-500 text-lg mt-0.5" />
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Encontramos <strong>{result.local.devices.length}</strong>{" "}
              dispositivos registrados con el mismo IMEI{" "}
              <strong>{result.imei}</strong>. Un IMEI no puede estar duplicado:
              revisa la información de cada uno y elimina el que no corresponda
              antes de poder recargar los datos.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {result.local.devices.map((dup) => (
              <div
                key={dup.id}
                className={`p-3 rounded-lg border ${themeClasses.cardBorder} ${cardBg}`}
              >
                <div className="mb-2">
                  <span className={`font-semibold text-sm ${themeClasses.text}`}>
                    {dup.name || "Sin nombre"}
                  </span>
                </div>
                <dl className="space-y-1 text-xs">
                  <Row label="ID" value={dup.id} t={themeClasses} />
                  <Row label="Compañía" value={companyLabel(dup.company_id)} t={themeClasses} />
                  <Row label="Vehículo" value={orDash(dup.vehicle_id)} t={themeClasses} />
                  <Row label="Marca" value={brandLabel(dup.brand_id)} t={themeClasses} />
                  <Row label="Estado" value={statusLabel(dup.status_id)} t={themeClasses} />
                </dl>
              </div>
            ))}
          </div>
        </div>
      );
    }

    const local = result.local.devices[0];
    const source = result.source;
    const meta = source?.meta;

    if (source?.skipped) {
      return (
        <div className="py-8 text-center">
          <i className="ri-information-line text-3xl text-blue-500" />
          <p className={`mt-2 text-sm ${themeClasses.text}`}>
            La comparación con el sistema de posiciones no está disponible en
            este entorno.
          </p>
          <p className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
            La recarga puede ejecutarse de todas formas.
          </p>
        </div>
      );
    }

    const rows: { label: string; localVal: string; sourceVal: string; diff: boolean }[] = [
      {
        label: "Device ID",
        localVal: orDash(local?.id),
        sourceVal: orDash(source?.device_id),
        diff: !!source?.device_id && !!local?.id && source.device_id !== local.id,
      },
      {
        label: "Vehículo",
        localVal: device.vehicle?.name && device.vehicle_id === local?.vehicle_id
          ? device.vehicle.name
          : orDash(local?.vehicle_id),
        sourceVal: meta?.vehicle_name || orDash(meta?.vehicle_id),
        diff: (local?.vehicle_id || null) !== (meta?.vehicle_id || null),
      },
      {
        label: "Remolque",
        localVal: orDash(local?.remolque_id),
        sourceVal: meta?.remolque_name || orDash(meta?.remolque_id),
        diff: (local?.remolque_id || null) !== (meta?.remolque_id || null),
      },
      {
        label: "Marca",
        localVal: brandLabel(local?.brand_id),
        sourceVal: brandLabel(meta?.brand_id),
        diff: (local?.brand_id || null) !== (meta?.brand_id || null),
      },
      {
        label: "Compañía",
        localVal: companyLabel(local?.company_id),
        sourceVal: companyLabel(meta?.company_id),
        diff: (local?.company_id || null) !== (meta?.company_id || null),
      },
      {
        label: "Estado",
        localVal: statusLabel(local?.status_id),
        sourceVal: statusLabel(meta?.status_id),
        diff: (local?.status_id || null) !== (meta?.status_id || null),
      },
    ];

    const anyDiff = rows.some((r) => r.diff);

    return (
      <div className="flex flex-col gap-4">
        <div
          className={`flex items-start gap-2 p-3 rounded-lg border ${
            anyDiff
              ? "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30"
              : "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30"
          }`}
        >
          <i
            className={`${anyDiff ? "ri-error-warning-line text-amber-500" : "ri-checkbox-circle-line text-green-500"} text-lg mt-0.5`}
          />
          <div className={`text-sm ${anyDiff ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"}`}>
            {anyDiff ? (
              "Detectamos que los datos guardados en el sistema de posiciones no corresponden con los de esta unidad. Si los datos de tu dispositivo son los correctos, presiona Recargar para sincronizarlos; si no, edita el dispositivo con la información correcta y vuelve a recargar."
            ) : (
              <>
                <p>
                  Los datos de esta unidad coinciden con el sistema de
                  posiciones.
                </p>
                <p className="mt-1 opacity-90">
                  Aun así puedes recargar: la sincronización fuerza al sistema de
                  posiciones a volver a leer y aplicar la información de este
                  dispositivo, asegurando que muestre los datos correctos de la
                  unidad.
                </p>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className={`flex items-center gap-1.5 self-start text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors`}
        >
          <i className={showDetails ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          {showDetails ? "Ocultar detalles técnicos" : "Ver detalles técnicos"}
          {anyDiff && !showDetails && (
            <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
          )}
        </button>

        {showDetails && (
          <div className={`rounded-lg border ${themeClasses.cardBorder} overflow-hidden`}>
            <div className={`grid grid-cols-3 gap-2 px-3 py-2 text-xs font-semibold ${cardBg} ${themeClasses.textSecondary}`}>
              <span>Campo</span>
              <span>Tu dispositivo</span>
              <span>Sistema de posiciones</span>
            </div>
            {rows.map((r) => (
              <div
                key={r.label}
                className={`grid grid-cols-3 gap-2 px-3 py-2 text-sm border-t ${themeClasses.cardBorder} ${r.diff ? "bg-amber-50/60 dark:bg-amber-500/5" : ""}`}
              >
                <span className={themeClasses.textSecondary}>{r.label}</span>
                <span className={`${themeClasses.text} break-all`}>{r.localVal}</span>
                <span className={`break-all ${r.diff ? "text-amber-600 dark:text-amber-400 font-medium" : themeClasses.text}`}>
                  {r.sourceVal}
                </span>
              </div>
            ))}
          </div>
        )}

        {source?.purged_old_device_id && (
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            Dispositivo anterior eliminado del sistema de posiciones:{" "}
            {source.purged_old_device_id}
          </p>
        )}

        {applied && (
          <p className="text-sm text-green-600 dark:text-green-400">
            <i className="ri-check-line mr-1" />
            Datos recargados correctamente.
          </p>
        )}

        {apply.isError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {getError(apply.error)}
          </p>
        )}
      </div>
    );
  };

  const canApply =
    !!result && !result.hasDuplicates && !inspect.isPending && !applied;

  const refreshButton = (
    <button
      onClick={handleApply}
      disabled={!canApply || apply.isPending}
      className="ml-2 bg-primary-500 hover:bg-primary-400 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 whitespace-nowrap"
    >
      <i className={`ri-refresh-line text-base ${apply.isPending ? "animate-spin" : ""}`} />
      <span>{apply.isPending ? "Recargando..." : "Recargar"}</span>
    </button>
  );

  const header = (
    <div className={`flex items-center gap-3 pb-4 mb-4 border-b ${themeClasses.cardBorder}`}>
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-500/10 text-primary-500">
        <i className="ri-router-line text-lg" />
      </div>
      <div className="min-w-0">
        <p className={`font-semibold ${themeClasses.text} truncate`}>
          {device.name || device.model || "Dispositivo"}
        </p>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          IMEI: {device.identifier || "—"}
        </p>
      </div>
    </div>
  );

  return (
    <Modal
      content={
        <div>
          {header}
          {renderBody()}
        </div>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title="Refrescar datos"
      width="max-w-3xl"
      buttonAction={refreshButton}
    />
  );
}

function Row({
  label,
  value,
  t,
}: {
  label: string;
  value: string;
  t: ThemeClasses;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className={t.textSecondary}>{label}</dt>
      <dd className={`${t.text} break-all text-right`}>{value}</dd>
    </div>
  );
}
